import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiMessageSquare, FiSend, FiUser, FiMail, FiPhone, FiClock, FiCheck, FiXCircle, FiArrowLeft, FiList, FiAlertCircle } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
console.log('🔧 Chat Management - API_URL:', API_URL); // Debug log

const Container = styled.div`
  display: flex;
  height: calc(100vh - 80px);
  background: #f8f9fa;
  position: relative;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const ConversationsList = styled.div`
  width: 350px;
  background: white;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;
    transform: ${props => props.$showOnMobile ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
    border-right: none;
  }
`;

const ConversationsHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #3498db;
  color: white;
  
  h2 {
    margin: 0;
    font-size: 18px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .stats {
    display: flex;
    gap: 15px;
    margin-top: 10px;
    font-size: 13px;
  }
  
  .stat {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const FilterTab = styled.button`
  flex: 1;
  padding: 12px;
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-bottom: ${props => props.active ? '2px solid #3498db' : '2px solid transparent'};
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? '#3498db' : '#666'};
  transition: all 0.2s;
  
  &:hover {
    background: white;
  }
`;

const ConversationsListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ConversationItem = styled.div`
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  background: ${props => props.active ? '#e3f2fd' : 'white'};
  transition: background 0.2s;
  
  &:hover {
    background: #f5f5f5;
  }
  
  .conversation-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  
  .customer-name {
    font-weight: 600;
    color: #333;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .time {
    font-size: 12px;
    color: #999;
  }
  
  .last-message {
    font-size: 13px;
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 8px;
  }
  
  .conversation-meta {
    display: flex;
    gap: 10px;
    font-size: 12px;
    color: #999;
  }
  
  .badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
  }
  
  .badge.waiting {
    background: #fff3cd;
    color: #856404;
  }
  
  .badge.replied {
    background: #d4edda;
    color: #155724;
  }
  
  .badge.closed {
    background: #f8d7da;
    color: #721c24;
  }
  
  .badge.ended {
    background: #6c757d;
    color: white;
  }
  
  .unread-count {
    background: #e74c3c;
    color: white;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
  }
`;

const MobileToggleButton = styled.button`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #3498db;
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
    cursor: pointer;
    z-index: 99;
    transition: all 0.3s ease;
    
    &:active {
      transform: scale(0.95);
    }
    
    svg {
      font-size: 24px;
    }
  }
`;

const MobileBackButton = styled.button`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: transparent;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    color: #3498db;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;
    
    &:hover {
      background: #f8f9fa;
    }
    
    svg {
      font-size: 18px;
    }
  }
`;

const ChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 100%;
    display: ${props => props.$hideOnMobile ? 'none' : 'flex'};
  }
`;

const ChatHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  
  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
  
  .customer-info {
    display: flex;
    align-items: center;
    gap: 15px;
  }
  
  .customer-name {
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
  
  .end-chat-button {
    padding: 8px 16px;
    background: #e74c3c;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 13px;
    transition: background 0.2s;
    
    &:hover {
      background: #c0392b;
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
  
  .customer-details {
    display: flex;
    gap: 20px;
    font-size: 13px;
    color: #666;
  }
  
  .detail-item {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
`;

const Message = styled.div`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  align-items: ${props => props.sender === 'customer' ? 'flex-start' : 'flex-end'};
  
  .message-bubble {
    max-width: 60%;
    padding: 12px 16px;
    border-radius: 16px;
    background: ${props => props.sender === 'customer' ? 'white' : '#3498db'};
    color: ${props => props.sender === 'customer' ? '#333' : 'white'};
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    
    @media (max-width: ${props => props.theme.breakpoints.md}) {
      max-width: 85%;
    }
  }
  
  .message-sender {
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 4px;
    color: #999;
  }
  
  .message-time {
    font-size: 11px;
    color: #999;
    margin-top: 4px;
  }
`;

const InputArea = styled.div`
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  background: white;
  
  .input-container {
    display: flex;
    gap: 10px;
  }
  
  input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 24px;
    font-size: 14px;
    outline: none;
    
    &:focus {
      border-color: #3498db;
    }
  }
  
  button {
    padding: 12px 24px;
    background: #3498db;
    color: white;
    border: none;
    border-radius: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    transition: background 0.2s;
    
    &:hover {
      background: #2980b9;
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  
  svg {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.3;
  }
  
  p {
    font-size: 16px;
  }
`;

const CustomerChatManagement = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showConversationsOnMobile, setShowConversationsOnMobile] = useState(true);
  const messagesEndRef = useRef(null);
  const pollingInterval = useRef(null);

  useEffect(() => {
    fetchConversations();
    
    // Poll for new messages every 5 seconds
    pollingInterval.current = setInterval(() => {
      fetchConversations();
      if (selectedConversation) {
        fetchMessages(selectedConversation.sessionId);
      }
    }, 5000);
    
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_URL}/admin/chat/conversations?status=${filter === 'all' ? '' : filter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setConversations(response.data.conversations);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (sessionId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_URL}/admin/chat/conversations/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const conversation = response.data.conversation;
        
        // Allow viewing ended conversations (just mark them as read-only)
        if (conversation.status === 'ended') {
          console.log('� Viewing ended conversation (read-only mode)');
          // Don't close - just show the messages in read-only mode
        }
        
        setMessages(response.data.messages);
        setSelectedConversation(conversation);
        scrollToBottom();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_URL}/admin/chat/conversations/${selectedConversation.sessionId}/reply`,
        { message: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setReplyText('');
        fetchMessages(selectedConversation.sessionId);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    }
  };

  const endChat = async () => {
    if (!selectedConversation) return;
    
    if (!window.confirm('Are you sure you want to end this chat? This will also close the chat for the customer.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_URL}/admin/chat/conversations/${selectedConversation.sessionId}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert('Chat ended successfully');
        
        // Clear the selected conversation immediately
        setSelectedConversation(null);
        setMessages([]);
        
        // Show conversations list on mobile
        setShowConversationsOnMobile(true);
        
        // Refresh conversations list to update status
        fetchConversations();
        
        console.log('✅ Chat ended and admin view cleared');
      }
    } catch (error) {
      console.error('Error ending chat:', error);
      alert('Failed to end chat');
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const formatTime = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return messageDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else {
      return messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'waiting_for_agent': { label: 'Waiting', class: 'waiting' },
      'agent_replied': { label: 'Replied', class: 'replied' },
      'closed': { label: 'Closed', class: 'closed' },
      'ended': { label: 'Ended', class: 'ended' },
      'active': { label: 'Active', class: 'waiting' }
    };
    
    const badge = badges[status] || badges['active'];
    return <span className={`badge ${badge.class}`}>{badge.label}</span>;
  };

  const filteredConversations = conversations.filter(conv => {
    if (filter === 'all') return true;
    return conv.status === filter;
  });

  const stats = {
    total: conversations.length,
    waiting: conversations.filter(c => c.status === 'waiting_for_agent').length,
    unread: conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0)
  };

  // Mobile handlers
  const handleSelectConversation = (sessionId) => {
    fetchMessages(sessionId);
    setShowConversationsOnMobile(false); // Hide conversation list on mobile when chat is selected
  };

  const handleBackToConversations = () => {
    setShowConversationsOnMobile(true);
    setSelectedConversation(null);
  };

  const toggleConversationsList = () => {
    setShowConversationsOnMobile(!showConversationsOnMobile);
  };

  return (
    <Container>
      <ConversationsList $showOnMobile={showConversationsOnMobile}>
        <ConversationsHeader>
          <h2>
            <FiMessageSquare />
            Customer Chats
          </h2>
          <div className="stats">
            <div className="stat">
              <FiMessageSquare />
              {stats.total} Total
            </div>
            <div className="stat">
              <FiClock />
              {stats.waiting} Waiting
            </div>
            {stats.unread > 0 && (
              <div className="stat">
                <FiCheck />
                {stats.unread} Unread
              </div>
            )}
          </div>
        </ConversationsHeader>
        
        <FilterTabs>
          <FilterTab active={filter === 'all'} onClick={() => setFilter('all')}>
            All
          </FilterTab>
          <FilterTab active={filter === 'waiting_for_agent'} onClick={() => setFilter('waiting_for_agent')}>
            Waiting
          </FilterTab>
          <FilterTab active={filter === 'agent_replied'} onClick={() => setFilter('agent_replied')}>
            Replied
          </FilterTab>
          <FilterTab active={filter === 'closed'} onClick={() => setFilter('closed')}>
            Closed
          </FilterTab>
        </FilterTabs>
        
        <ConversationsListScroll>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
              Loading conversations...
            </div>
          ) : filteredConversations.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
              No conversations found
            </div>
          ) : (
            filteredConversations.map(conv => (
              <ConversationItem
                key={conv._id}
                active={selectedConversation?.sessionId === conv.sessionId}
                onClick={() => handleSelectConversation(conv.sessionId)}
              >
                <div className="conversation-header">
                  <div className="customer-name">
                    <FiUser />
                    {conv.customerName || 'Anonymous'}
                  </div>
                  <div className="time">
                    {formatTime(conv.lastCustomerMessageAt)}
                  </div>
                </div>
                
                {conv.lastMessage && (
                  <div className="last-message">
                    {conv.lastMessage.message}
                  </div>
                )}
                
                <div className="conversation-meta">
                  {getStatusBadge(conv.status)}
                  {conv.queryType && <span>{conv.queryType.replace('_', ' ')}</span>}
                  {conv.unreadCount > 0 && (
                    <span className="unread-count">{conv.unreadCount} new</span>
                  )}
                </div>
              </ConversationItem>
            ))
          )}
        </ConversationsListScroll>
      </ConversationsList>
      
      <ChatArea $hideOnMobile={showConversationsOnMobile}>
        {selectedConversation ? (
          <>
            <ChatHeader>
              <div className="header-top">
                <MobileBackButton onClick={handleBackToConversations}>
                  <FiArrowLeft />
                  Back
                </MobileBackButton>
                <div className="customer-info">
                  <div className="customer-name">
                    {selectedConversation.customerName || 'Anonymous Customer'}
                  </div>
                  {getStatusBadge(selectedConversation.status)}
                </div>
                {selectedConversation.status !== 'ended' && (
                  <button className="end-chat-button" onClick={endChat}>
                    <FiXCircle />
                    End Chat
                  </button>
                )}
              </div>
              <div className="customer-details">
                {selectedConversation.customerEmail && (
                  <div className="detail-item">
                    <FiMail />
                    {selectedConversation.customerEmail}
                  </div>
                )}
                {selectedConversation.customerPhone && (
                  <div className="detail-item">
                    <FiPhone />
                    {selectedConversation.customerPhone}
                  </div>
                )}
                {selectedConversation.queryType && (
                  <div className="detail-item">
                    Query: {selectedConversation.queryType.replace('_', ' ')}
                  </div>
                )}
              </div>
            </ChatHeader>
            
            {selectedConversation?.status === 'ended' && (
              <div style={{
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                padding: '12px',
                margin: '16px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#92400e'
              }}>
                <FiAlertCircle />
                <span>This conversation has ended. You can view the history but cannot send new messages.</span>
              </div>
            )}
            
            <MessagesArea>
              {messages.map((msg, index) => (
                <Message key={index} sender={msg.sender}>
                  {msg.sender === 'agent' && (
                    <div className="message-sender">{msg.agentName || 'Agent'}</div>
                  )}
                  <div className="message-bubble">
                    {msg.message}
                  </div>
                  <div className="message-time">
                    {formatTime(msg.createdAt)}
                  </div>
                </Message>
              ))}
              <div ref={messagesEndRef} />
            </MessagesArea>
            
            <InputArea>
              <div className="input-container">
                <input
                  type="text"
                  placeholder={selectedConversation?.status === 'ended' ? 'This conversation has ended' : 'Type your reply...'}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      sendReply();
                    }
                  }}
                  disabled={selectedConversation?.status === 'ended'}
                />
                <button 
                  onClick={sendReply} 
                  disabled={!replyText.trim() || selectedConversation?.status === 'ended'}
                >
                  <FiSend />
                  Send
                </button>
              </div>
            </InputArea>
          </>
        ) : (
          <EmptyState>
            <FiMessageSquare />
            <p>Select a conversation to start chatting</p>
          </EmptyState>
        )}
      </ChatArea>
      
      {selectedConversation && (
        <MobileToggleButton onClick={toggleConversationsList}>
          <FiList />
        </MobileToggleButton>
      )}
    </Container>
  );
};

export default CustomerChatManagement;
